// Helpers used across screens. Mirrors src/utils/helpers.ts where applicable
// (time category, format, battery color).

export type TimeCategory = 'recent' | 'medium' | 'old'

const TIME_THRESHOLDS = {
  recent: 10,   // < 10 min
  medium: 60,   // 10-60 min
} as const

export function getTimeCategory(timestamp: string | null | undefined): TimeCategory {
  if (!timestamp) return 'old'
  const minutes = (Date.now() - Date.parse(timestamp)) / 60_000
  if (minutes < TIME_THRESHOLDS.recent) return 'recent'
  if (minutes < TIME_THRESHOLDS.medium) return 'medium'
  return 'old'
}

export function timeCategoryColor(c: TimeCategory): string {
  switch (c) {
    case 'recent': return 'var(--color-statusGreen)'
    case 'medium': return 'var(--color-statusYellow)'
    case 'old':    return 'var(--color-statusRed)'
  }
}

export function formatTimeAgo(timestamp: string | null | undefined): string {
  if (!timestamp) return '—'
  const diff = Date.now() - Date.parse(timestamp)
  if (diff < 60_000) return 'Just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} Min ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

export function batteryColor(level: number): string {
  if (level >= 60) return 'var(--color-statusGreen)'
  if (level >= 30) return 'var(--color-statusYellow)'
  return 'var(--color-statusRed)'
}

// Shift helpers — mirror src/utils/helpers.ts so web's TagBoard scopes
// Tagged In / Tagged Out to the current shift the same way mobile does.
export type Shift = 'Morning' | 'Night'

export function getCurrentShift(): Shift {
  const hour = new Date().getHours()
  return hour >= 6 && hour < 18 ? 'Morning' : 'Night'
}

export function getShiftTimeRange(shift: Shift, date?: Date): {start: string; end: string} {
  const d = date ?? new Date()
  const start = new Date(d)
  const end = new Date(d)

  if (shift === 'Morning') {
    start.setHours(6, 0, 0, 0)
    end.setHours(18, 0, 0, 0)
  } else {
    if (d.getHours() < 6) {
      // After midnight, still in the previous day's night shift.
      start.setDate(start.getDate() - 1)
      start.setHours(18, 0, 0, 0)
      end.setHours(6, 0, 0, 0)
    } else {
      start.setHours(18, 0, 0, 0)
      end.setDate(end.getDate() + 1)
      end.setHours(6, 0, 0, 0)
    }
  }

  return {start: start.toISOString(), end: end.toISOString()}
}
